'use client';

/**
 * Client helper for minting an ephemeral GUEST account so an anonymous visitor
 * can use the apps without signing up. Used by the splash "Open app" CTA and by
 * the first-write safety net (`makeFetchWithGuestMint`).
 *
 * On success the caller should do a FULL navigation (`window.location.assign`)
 * rather than a client-side `router.push`, so the server picks up the freshly
 * set guest session cookie when it renders the destination.
 */

export type StartGuestResult =
  | { ok: true; userId: string; workspaceId: string | null; alreadyAuthed?: boolean }
  | { ok: false; status: number; code?: string };

export async function startGuest(body: {
  appId?: string;
  sourceWorkspaceId?: string;
  turnstileToken?: string | null;
}): Promise<StartGuestResult> {
  let res: Response;
  try {
    res = await fetch('/api/auth/guest', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, status: 0 };
  }
  if (!res.ok) {
    let code: string | undefined;
    try {
      code = ((await res.json()) as { code?: string })?.code;
    } catch {
      /* non-JSON error body */
    }
    return { ok: false, status: res.status, code };
  }
  const data = (await res.json()) as {
    userId: string;
    workspaceId?: string | null;
    alreadyAuthed?: boolean;
  };
  return {
    ok: true,
    userId: data.userId,
    workspaceId: data.workspaceId ?? null,
    alreadyAuthed: data.alreadyAuthed,
  };
}

/**
 * Where to land after minting: the guest's own clone if one was created, else
 * fall back to the `/?app=` server redirect (which resolves the workspace).
 */
export function guestLandingHref(workspaceId: string | null, appId: string): string {
  return workspaceId
    ? `/workspace/${workspaceId}`
    : `/?app=${encodeURIComponent(appId)}`;
}
